import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { batch, useDispatch } from 'react-redux';
import { Box, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import {
  ACTION_OPTION_TYPE,
  Actions,
  CombinedForm,
  Form,
  FormForge,
  ListViewer,
  MarkDown,
  ModuleHeader,
  PromptInput,
  ReviewCard,
  SafeBoundary,
  Sod,
  Table,
} from '../../../../shared/components';
import {
  CF_NEU_STL,
  CHAT_BLOCK,
  CHAT_MODE,
  COPILOT_WORKFLOWS,
  IS_ELIGIBLE,
  RENDER_FORM_EXPANDED,
  RENDER_NORMAL,
  testIds,
} from '../../../../shared/constants';
import {
  useGetAssitantReply,
  useSaviNavigator,
  useTypedSelector,
} from '../../../../shared/hooks';
import {
  type ActionBlock,
  type ChatMessage,
  type ChatMode,
  type CombinedFormBlock,
  type CustomBlock,
  type FormBlock,
  type FormForgeBlock,
  type GeneratingBlocks,
  type MarkdownBlock,
  type PanelBlocks,
  type ReviewBlock,
  type SodBlock,
  type TableBlock,
  type WorkflowInfo,
} from '../../../../shared/types';
import { chatModuleActions } from '../../data-store/chatModuleSlice';
import {
  appendThread,
  getErrorBlock,
  getMarkdownBlock,
  getWorkflowType,
  updateThread,
} from '../../utils';

import ResponseActionBar from './ResponseActionBar';
import ResponseProgressBar from './ResponseProgressBar';
import ResponseWrapper from './ResponseWrapper';

import './ChatCenterPanel.css';

const SCROLL_KEY_TEXT = 'workflow-scroll-key-';

export interface ChatRef {
  triggerAssistant: (chatBlock: ChatMessage<ChatMode>) => void;
  scrollToBottom: () => void;
  resetChat: () => void;
}

interface ChatCenterPanelProps {
  query?: string;
  workflow?: string;
  threadId: string;
  userId: string | number;
}

/**
 * ChatCenterPanel
 */
const ChatCenterPanel = forwardRef<ChatRef, ChatCenterPanelProps>(
  ({ query, workflow, threadId, userId }, ref) => {
    const dispatch = useDispatch();
    const navigator = useSaviNavigator();

    const { renderMode, rightPanel, scrollToStep, workflowInfo } =
      useTypedSelector((state) => state.savicopilot.chatModule);

    // States
    const [thread, setThread] = useState<ChatMessage<ChatMode>[]>([]);
    const [currentScrolled, setCurrentScrolled] = useState<string | null>(null);

    // References
    const chatAreaRef = useRef<HTMLDivElement | null>(null);

    // Constants
    const isWorkflow = useMemo(
      () =>
        Boolean(
          workflow &&
            Object.values(COPILOT_WORKFLOWS)
              .map((w) => w.key)
              .includes(workflow)
        ),
      [workflow]
    );
    const copilotWorkflow = useMemo(() => {
      const wf = Object.values(COPILOT_WORKFLOWS).find(
        (w) => w.key === workflow
      );

      return wf ?? COPILOT_WORKFLOWS.DEFAULT;
    }, [workflow]);

    const {
      handleAssistant,
      stopCall: killStream,
      isLoading: isStreaming,
    } = useGetAssitantReply();

    // Chat utils
    const scrollToBottom = useCallback(() => {
      if (chatAreaRef.current) {
        chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
      }
    }, []);

    const resetChat = useCallback(() => {
      batch(() => {
        dispatch(chatModuleActions.setRenderMode(RENDER_NORMAL));
        dispatch(chatModuleActions.setRightPanel(null));
      });

      setThread([]);
      killStream();
    }, [dispatch, killStream]);

    // Waypoint utils
    const loadWorkFlow = useCallback(
      (chatWorkflowInfo: WorkflowInfo | undefined) => {
        if (!chatWorkflowInfo) {
          batch(() => {
            dispatch(chatModuleActions.setScrollToStep(null));
            dispatch(chatModuleActions.setWorkflowInfo(null));
            dispatch(chatModuleActions.setRightPanel(null));
          });

          return;
        }

        dispatch(chatModuleActions.setWorkflowInfo(chatWorkflowInfo));
      },
      [dispatch]
    );

    // Stream utils
    const handleAssitantStream = useCallback(
      (streamResponse: ChatMessage<ChatMode>) => {
        appendThread(streamResponse, setThread);
        loadWorkFlow(streamResponse.metadata.workflowInfo);
      },

      [loadWorkFlow]
    );

    const triggerAssistant = useCallback(
      (chatBlock: ChatMessage<ChatMode>) => {
        const {
          metadata: { timestamp, workflowInfo: metadataWorkflow },
          ...restMessage
        } = updateThread(chatBlock, setThread);
        const sanitizedChatBlock = {
          ...restMessage,
          metadata: { timestamp, workflowInfo: metadataWorkflow },
        };

        handleAssistant(sanitizedChatBlock, handleAssitantStream).catch(
          (error: string) =>
            updateThread(getErrorBlock(threadId, String(error)), setThread)
        );
      },
      [handleAssistant, handleAssitantStream, threadId]
    );

    // Effects
    useEffect(() => {
      if (isWorkflow && copilotWorkflow.prompt) {
        triggerAssistant(getMarkdownBlock(threadId, copilotWorkflow.prompt));

        return;
      }

      if (query && query.trim() !== '') {
        triggerAssistant(getMarkdownBlock(threadId, query));
      }
    }, [isWorkflow, query, threadId, copilotWorkflow, triggerAssistant]);

    useEffect(() => {
      scrollToBottom();
    }, [thread, scrollToBottom]);

    useEffect(() => {
      if (scrollToStep !== null && chatAreaRef.current) {
        const workflowSrollKey = `${SCROLL_KEY_TEXT}${scrollToStep}`;
        const targetElement = chatAreaRef.current.querySelector(
          `[data-scroll-id="${workflowSrollKey}"]`
        );

        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }

        setCurrentScrolled(workflowSrollKey);
        dispatch(chatModuleActions.setScrollToStep(null));
      }
    }, [dispatch, scrollToStep]);

    useEffect(() => {
      if (currentScrolled) {
        // Time has been set for animation time period
        const timeout = setTimeout(() => {
          setCurrentScrolled(null);
        }, 2000);

        return () => clearTimeout(timeout);
      }

      return undefined;
    }, [currentScrolled]);

    useEffect(() => {
      dispatch(chatModuleActions.setIsStreaming(isStreaming));
    }, [dispatch, isStreaming]);

    useImperativeHandle(
      ref,
      () => ({
        triggerAssistant,
        scrollToBottom,
        resetChat,
      }),
      [triggerAssistant, scrollToBottom, resetChat]
    );

    // Flow utils
    const handleExpand = useCallback(
      (
        message: ChatMessage<ChatMode>,
        typedBlock: PanelBlocks,
        metadataConfigs?: {
          isEdited?: boolean;
          actualKey?: string;
        }
      ) => {
        batch(() => {
          dispatch(
            chatModuleActions.setRightPanel({
              ...message,
              blocks: [typedBlock],
              metadata: {
                ...message.metadata,
                ...metadataConfigs,
              },
            })
          );
          dispatch(chatModuleActions.setRenderMode(RENDER_FORM_EXPANDED));
        });
      },
      [dispatch]
    );

    const getSubmitPayload = useCallback(
      (
        message: ChatMessage<ChatMode>,
        metadataConfigs?: {
          isEdited?: boolean;
          actualKey?: string;
        }
      ) => ({
        ...message,
        mode: CHAT_MODE.USER,
        metadata: {
          ...message.metadata,
          ...metadataConfigs,
          timestamp: new Date().toISOString(),
        },
      }),
      []
    );

    const closeRightPanel = useCallback(() => {
      batch(() => {
        dispatch(chatModuleActions.setRenderMode(RENDER_NORMAL));
        dispatch(chatModuleActions.setRightPanel(null));
      });
    }, [dispatch]);

    const triggerPrompt = useCallback(
      (userInput: string) => {
        triggerAssistant(getMarkdownBlock(threadId, userInput));

        closeRightPanel();
        scrollToBottom();
      },
      [scrollToBottom, threadId, triggerAssistant, closeRightPanel]
    );

    const getModuleHeaderTitle = () => {
      if (!workflowInfo) {
        return COPILOT_WORKFLOWS.DEFAULT.chatTitle;
      }

      const workflowKey = getWorkflowType(workflowInfo.workflowName);

      return (
        Object.values(COPILOT_WORKFLOWS).find((w) => w.key === workflowKey)
          ?.chatTitle ?? COPILOT_WORKFLOWS.DEFAULT.chatTitle
      );
    };

    return (
      <Box
        tag='div'
        className={cx(
          'ChatCenterPanel',
          renderMode === RENDER_FORM_EXPANDED &&
            'ChatCenterPanel--isFormExpanded'
        )}
        dataTestId={testIds.CHAT_CENTER_PANEL}>
        {/* Header */}
        <ModuleHeader
          title={getModuleHeaderTitle()}
          progress={5}
          isCollapsed={renderMode === RENDER_FORM_EXPANDED}
          onReset={() => {
            batch(() => {
              dispatch(chatModuleActions.setRenderMode(RENDER_NORMAL));
              dispatch(chatModuleActions.setRightPanel(null));
            });
          }}
          onClose={() => {
            killStream();
            navigator.goBack();
          }}
        />
        {/* Chat Area */}
        <div ref={chatAreaRef} className='ChatCenterPanel-chatWrapper'>
          <div className='ChatCenterPanel-chatArea'>
            {thread.map((message, idx) => {
              const key = message.metadata.messageId;
              const viewKey = rightPanel?.metadata.messageId;
              const isRightWindowOpen = Boolean(rightPanel !== null);
              const workflowSrollKey = `${SCROLL_KEY_TEXT}${
                message.metadata.workflowInfo?.currentStep ?? ''
              }`;

              if (!key) {
                return null;
              }

              if (message.mode === CHAT_MODE.USER) {
                return message.blocks.map((block) => {
                  if (block.type === CHAT_BLOCK.MARKDOWN) {
                    const typedBlock = block as MarkdownBlock;

                    return (
                      <div
                        key={block.blockId}
                        data-scroll-id={workflowSrollKey}
                        className='ChatCenterPanel-isUser'
                        data-testid={testIds.CHAT_MESSAGE_USER}>
                        <MarkDown key={block.blockId}>
                          {typedBlock.content}
                        </MarkDown>
                      </div>
                    );
                  }

                  return null;
                });
              }

              if (message.mode === CHAT_MODE.ASSISTANT) {
                return (
                  <ResponseWrapper
                    id={workflowSrollKey}
                    key={key}
                    className={
                      currentScrolled === workflowSrollKey
                        ? 'ChatCenterPanel-chatArea--isScrolled'
                        : ''
                    }>
                    {message.blocks.map((block, _idx) => {
                      const isLast = Boolean(
                        idx === thread.length - 1 &&
                          _idx === message.blocks.length - 1 &&
                          rightPanel === null
                      );
                      const viewBlockKey = rightPanel?.blocks[0].blockId;
                      const sharedMetadata = {
                        isEdited: !isLast,
                        actualKey: key,
                      };

                      if (!block.blockId) {
                        return null;
                      }

                      // When adding here, add to `Right Panel` also
                      if (block.type === CHAT_BLOCK.MARKDOWN) {
                        const typedBlock = block as MarkdownBlock;

                        return (
                          <MarkDown key={block.blockId}>
                            {typedBlock.content}
                          </MarkDown>
                        );
                      }

                      if (block.type === CHAT_BLOCK.ACTIONS) {
                        const typedBlock = block as ActionBlock;

                        return (
                          <SafeBoundary key={block.blockId}>
                            <Actions
                              id={block.blockId}
                              options={typedBlock.options}
                              isDisabled={
                                isStreaming ||
                                message.metadata.isWfTillNowCompleted
                              }
                              onSubmit={(option) => {
                                if (
                                  option.type === ACTION_OPTION_TYPE.SUGGESTION
                                ) {
                                  triggerPrompt(option.label);

                                  return;
                                }

                                triggerAssistant({
                                  ...getSubmitPayload(message, sharedMetadata),
                                  blocks: [
                                    {
                                      ...typedBlock,
                                      target: option,
                                    },
                                  ],
                                });
                              }}
                            />
                          </SafeBoundary>
                        );
                      }

                      if (block.type === CHAT_BLOCK.FORM) {
                        const typedBlock = block as FormBlock;

                        return (
                          <SafeBoundary key={block.blockId}>
                            <Form
                              id={block.blockId}
                              isLast={isLast}
                              isViewing={
                                key === viewKey &&
                                block.blockId === viewBlockKey
                              }
                              isRightWindowOpen={isRightWindowOpen}
                              isProcessing={
                                isStreaming ||
                                message.metadata.isWfTillNowCompleted
                              }
                              isChanged={message.metadata.isChanged}
                              resetKey={message.metadata.resetKey}
                              workflowType={getWorkflowType(
                                message.metadata.workflowInfo?.workflowName
                              )}
                              title={typedBlock.title}
                              data={typedBlock.elements}
                              actions={typedBlock.actions}
                              onInlineExpand={closeRightPanel}
                              retentions={typedBlock.retentions}
                              onExpand={(retentions) => {
                                handleExpand(
                                  message,
                                  { ...typedBlock, retentions },
                                  sharedMetadata
                                );
                              }}
                              onSubmit={(formData) => {
                                triggerAssistant({
                                  ...getSubmitPayload(message, sharedMetadata),
                                  mode: CHAT_MODE.USER,
                                  blocks: [
                                    {
                                      ...typedBlock,
                                      elements: formData,
                                    },
                                  ],
                                });
                              }}
                            />
                          </SafeBoundary>
                        );
                      }

                      if (block.type === CHAT_BLOCK.FORMFORGE) {
                        const typedBlock = block as FormForgeBlock;

                        return (
                          <SafeBoundary key={block.blockId}>
                            <FormForge
                              id={block.blockId}
                              isLast={isLast}
                              isViewing={
                                key === viewKey &&
                                block.blockId === viewBlockKey
                              }
                              isRightWindowOpen={isRightWindowOpen}
                              isProcessing={
                                isStreaming ||
                                message.metadata.isWfTillNowCompleted
                              }
                              isChanged={message.metadata.isChanged}
                              resetKey={message.metadata.resetKey}
                              retentions={typedBlock.retentions}
                              currentSelection={typedBlock.selections}
                              onInlineExpand={closeRightPanel}
                              onExpand={(retentions) => {
                                handleExpand(
                                  message,
                                  {
                                    ...typedBlock,
                                    retentions,
                                  },
                                  sharedMetadata
                                );
                              }}
                              onSubmit={(formData) => {
                                triggerAssistant({
                                  ...getSubmitPayload(message, sharedMetadata),
                                  blocks: [
                                    {
                                      ...typedBlock,
                                      formData,
                                    },
                                  ],
                                });
                              }}
                            />
                          </SafeBoundary>
                        );
                      }

                      if (block.type === CHAT_BLOCK.COMBINEDFORM) {
                        const typedBlock = block as CombinedFormBlock;

                        return (
                          <SafeBoundary key={block.blockId}>
                            <CombinedForm
                              id={block.blockId}
                              title={typedBlock.title}
                              isLast={isLast}
                              isViewing={
                                key === viewKey &&
                                block.blockId === viewBlockKey
                              }
                              isRightWindowOpen={isRightWindowOpen}
                              isProcessing={
                                isStreaming ||
                                message.metadata.isWfTillNowCompleted
                              }
                              isChanged={message.metadata.isChanged}
                              resetKey={message.metadata.resetKey}
                              userId={userId}
                              retentions={typedBlock.retentions}
                              customEndpoint={typedBlock.customEndpoint}
                              formforgeEndpoint={typedBlock.formforgeEndpoint}
                              onInlineExpand={closeRightPanel}
                              onExpand={(customFormData, externalFormData) => {
                                handleExpand(
                                  message,
                                  {
                                    ...typedBlock,
                                    retentions: {
                                      customFormData,
                                      externalFormData,
                                    },
                                  },
                                  sharedMetadata
                                );
                              }}
                              onSubmit={(customFormData, externalFormData) => {
                                triggerAssistant({
                                  ...getSubmitPayload(message, sharedMetadata),
                                  blocks: [
                                    {
                                      ...typedBlock,
                                      custom: {
                                        ...typedBlock.custom,
                                        formJson: customFormData,
                                      },
                                      external: {
                                        ...typedBlock.external,
                                        formData: externalFormData,
                                      },
                                    },
                                  ],
                                });
                              }}
                            />
                          </SafeBoundary>
                        );
                      }

                      if (block.type === CHAT_BLOCK.CUSTOM) {
                        const typedBlock = block as CustomBlock;

                        return (
                          <SafeBoundary key={block.blockId}>
                            <ListViewer
                              id={block.blockId}
                              isLast={isLast}
                              isViewing={
                                key === viewKey &&
                                block.blockId === viewBlockKey
                              }
                              isRightWindowOpen={isRightWindowOpen}
                              isProcessing={
                                isStreaming ||
                                message.metadata.isWfTillNowCompleted
                              }
                              isChanged={message.metadata.isChanged}
                              resetKey={message.metadata.resetKey}
                              variant={typedBlock.variant}
                              workflowType={getWorkflowType(
                                message.metadata.workflowInfo?.workflowName
                              )}
                              endpointDetails={typedBlock.endpoint}
                              retentions={typedBlock.retentions}
                              onInlineExpand={closeRightPanel}
                              onExpand={(retentions) => {
                                handleExpand(
                                  message,
                                  { ...typedBlock, retentions },
                                  sharedMetadata
                                );
                              }}
                              onSubmit={(selections) => {
                                triggerAssistant({
                                  ...getSubmitPayload(message, sharedMetadata),
                                  blocks: [
                                    {
                                      ...typedBlock,
                                      selections,
                                    },
                                  ],
                                });
                              }}
                            />
                          </SafeBoundary>
                        );
                      }

                      if (block.type === CHAT_BLOCK.REVIEW) {
                        const typedBlock = block as ReviewBlock;

                        return (
                          <SafeBoundary key={block.blockId}>
                            <ReviewCard
                              type={typedBlock.variant}
                              stages={typedBlock.stages}
                            />
                          </SafeBoundary>
                        );
                      }

                      if (block.type === CHAT_BLOCK.TABLE) {
                        const typedBlock = block as TableBlock;

                        return (
                          <SafeBoundary key={block.blockId}>
                            <Table
                              headers={typedBlock.headers}
                              rows={typedBlock.rows}
                              options={typedBlock.options}
                            />
                          </SafeBoundary>
                        );
                      }

                      if (block.type === CHAT_BLOCK.SOD) {
                        const typedBlock = block as SodBlock;

                        return (
                          <SafeBoundary key={block.blockId}>
                            <Sod
                              id={block.blockId}
                              isLast={isLast}
                              isViewing={
                                key === viewKey &&
                                block.blockId === viewBlockKey
                              }
                              isRightWindowOpen={isRightWindowOpen}
                              isChanged={message.metadata.isChanged}
                              resetKey={message.metadata.resetKey}
                              data={typedBlock.object}
                              onInlineExpand={closeRightPanel}
                            />
                          </SafeBoundary>
                        );
                      }

                      return null;
                    })}
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {renderMode === RENDER_NORMAL && IS_ELIGIBLE && (
                      <ResponseActionBar />
                    )}
                  </ResponseWrapper>
                );
              }

              if (message.mode === CHAT_MODE.ACTION) {
                return (
                  <ResponseProgressBar
                    key={key}
                    id={workflowSrollKey}
                    block={message.blocks[0] as GeneratingBlocks}
                  />
                );
              }

              return null;
            })}
          </div>
        </div>
        {/* Prompt */}
        <Box tag='div' className='ChatCenterPanel-promptWrapper'>
          <PromptInput
            placeholder={copilotWorkflow.inputPlaceholder}
            dataTestId={testIds.GEN_PROMPT_INPUT_ID}
            onSubmit={(userInput) => triggerPrompt(userInput)}
            onAddFiles={() => resetChat()}
            onStop={() => killStream()}
            onAudio={() => {}}
            isProcessing={isStreaming}
          />
          <Typography kind='body3' color={CF_NEU_STL}>
            <FormattedMessage
              id='IQVTGh'
              defaultMessage='Savi is always learning new things. Results may vary.'
              description='Disclaimer message shown at the bottom of chat center panel in Savi Copilot'
            />
          </Typography>
        </Box>
      </Box>
    );
  }
);

ChatCenterPanel.defaultProps = {
  query: undefined,
  workflow: undefined,
};

export default memo(ChatCenterPanel);
